// src/modules/app-views/_apps/appmulti/orders/pages/Document/tabs/OrderTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Badge, Card, Descriptions, Typography } from 'antd';
import dayjs from 'dayjs';
import { useDocumentContext } from 'components/core/DocumentProvider';
import translateX from 'utils/translateX';
import { parsePhoneToDisplay } from 'utils/helpers';
import financialService from 'services/financialService';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const { Link } = Typography;

const STYLES = {
  travellersItem: { display: 'block' },
  cardBottom: { marginBottom: 16 },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const OrderTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data } = useDocumentContext();
  const record = data || {};
  const receivable = record?.__receivables?.[0] || {};
  const plan = record?.__plan || {};
  const operator = record?.__operator || {};
  const buyer = record?.__buyer || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Card title={translateX('acquired_package')} style={STYLES.cardBottom}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label={translateX('plan')}>
            {`${plan?.name || '—'} | ${operator?.name || '—'}`}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('due_date')}>
            {receivable?.dueDate ? dayjs(receivable.dueDate).format('L') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('value')}>
            {record?.totalInOrderDate?.totalToPay != null
              ? financialService.formatNumber(record.totalInOrderDate.totalToPay, {
                  decimals: 2,
                  locale: 'pt-BR',
                })
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('invoice_bill')}>
            {receivable?._metadata?.counter ? `#${receivable._metadata.counter}` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('status')}>
            {translateX(receivable?.status || record?.__statusView || record?.status)}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('payment_link')}>
            {receivable?.paymentUrl ? (
              <Link href={receivable.paymentUrl} target="_blank">
                {translateX('view_billing')}
              </Link>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          {receivable?.bankSlipLink ? (
            <Descriptions.Item label={translateX('bank_slip')}>
              <Link href={receivable.bankSlipLink} target="_blank">
                {translateX('open')}
              </Link>
            </Descriptions.Item>
          ) : null}
          {receivable?.transactionReceiptUrl ? (
            <Descriptions.Item label={translateX('transaction_receipt')}>
              <Link href={receivable.transactionReceiptUrl} target="_blank">
                {translateX('open')}
              </Link>
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      </Card>

      <Card title={translateX('summary')}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label={translateX('destiny')}>
            {`${translateX(record?.destiny)} — ${record?.days ?? 0} ${translateX('days')}`}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('travel_period')}>
            {`${record?.departure ? dayjs(record.departure).format('L') : '—'} → ${record?.arrival ? dayjs(record.arrival).format('L') : '—'}`}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('buyer')} span={2}>
            {`${buyer?.name || '—'} · ${parsePhoneToDisplay(buyer?.mobile || buyer?.phone) || '—'} · ${buyer?.email || '—'}`}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('travellers')} span={2}>
            {(record?.travellers || []).map((t, i) => (
              <Badge
                key={i}
                status={t.ageInTravelDate >= 60 ? 'warning' : 'success'}
                text={`${t.name || '—'}, ${t.ageInTravelDate ?? ''} ${translateX('years_old')}`}
                style={STYLES.travellersItem}
              />
            ))}
          </Descriptions.Item>
          {(record?.emergencyContacts || []).length ? (
            <Descriptions.Item label={translateX('emergency_contacts')} span={2}>
              {record.emergencyContacts.map((c, i) => (
                <div key={i}>
                  {`${c.name} · ${parsePhoneToDisplay(c.phone) || '—'}${c.relationship ? ` · ${c.relationship}` : ''}`}
                </div>
              ))}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      </Card>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default OrderTab;
